import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { GlobalI18n } from '@settings/global-i18n';
import { Module } from '@shared/models/module-name';
import { MasterDataService } from '@shared/data-provider/master-data-api/api/masterData.service';
import { convertDateToUTC } from '@shared/functions/dateFunctions';
import { NzModalService } from 'ng-zorro-antd/modal';
import { map } from 'rxjs';
import { MasterDataFunctionsService } from '../../services/master-data.service';
import { CustomFormValidators } from '@shared/custom-form-validators';
import { Delay } from '@shared/data-provider/master-data-api/model/delay';
import { SettingsService } from '@core/services/settings/settings.service';

interface ExtendedDelay extends Delay {
	nameEn?: string;
	nameRu?: string;
	nameLocal?: string;
	dtRangeStart?: Date;
	dtRangeFinish?: Date;
}

@Component({
	selector: 'app-delays',
	templateUrl: './delays.component.html',
	styleUrls: ['./delays.component.less'],
})
export class DelaysComponent implements OnInit {
	constructor(
		private apiMasterData: MasterDataService,
		private settingsService: SettingsService,
		private modal: NzModalService,
		private fb: UntypedFormBuilder,
		private globalI18n: GlobalI18n,
		private functionsService: MasterDataFunctionsService
	) {}

	private formValidators: CustomFormValidators = new CustomFormValidators();

	private _list: ExtendedDelay[] = [];
	selectedItem: ExtendedDelay;
	form: FormGroup;

	settingValue = {
		loadingList: false,
		loadingModal: false,
		saving: false,
		searchName: '',
		isVisibleModal: false,
		pageIndex: 1,
	};

	listOfColumn = [];

	ngOnInit() {
		this.loadList();
		this.listOfColumn = [
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnChar'),
				compare: (a: ExtendedDelay, b: ExtendedDelay) => a.iataChar.localeCompare(b.iataChar),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnName'),
				compare: (a: ExtendedDelay, b: ExtendedDelay) => a.nameLocal.localeCompare(b.nameLocal),
				priority: false,
			},
			{
				title: this.globalI18n.getMessage(Module.MasterData, 'columnLastUpdate'),
				compare: (a: ExtendedDelay, b: ExtendedDelay) => Date.parse(a.lastupdate) - Date.parse(b.lastupdate),
				priority: false,
			},
		];
	}

	loadList() {
		this.settingValue.loadingList = true;
		this.apiMasterData.selectRecords('delays').subscribe((data: Delay[]) => {
			this._list = data.map(item => {
				return {
					...item,
					nameEn: item.name[0],
					nameRu: item.name[1],
					nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
					dtRangeStart: new Date(item.dtRange[0]),
					dtRangeFinish: new Date(item.dtRange[1]),
				};
			});
			this.settingValue.loadingList = false;
		});
	}

	get listOfDisplay(): ExtendedDelay[] {
		// При установленных фильтрах необходимо сбросить пагинацию на 1 страницу
		if (this.settingValue.searchName.length !== 0) {
			this.onPageIndexChanged(1);
		}
		return this._list.filter(item => {
			return (
				item.nameRu.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1 ||
				item.nameEn.toLowerCase().indexOf(this.settingValue.searchName.toLowerCase()) != -1
			);
		});
	}

	openItem(id: number) {
		this.settingValue.loadingList = true;
		return this.apiMasterData
			.selectRecord('delays', id.toString())
			.pipe(
				map((item: Delay) => {
					return {
						...item,
						nameEn: item.name[0],
						nameRu: item.name[1],
						nameLocal: this.settingsService.language == 'EN' ? item.name[0] : item.name[1],
						dtRangeStart: new Date(item.dtRange[0]),
						dtRangeFinish: new Date(item.dtRange[1]),
					};
				})
			)
			.subscribe((data: ExtendedDelay) => {
				this.selectedItem = data;
				this.form = this.createForm(data);
				this.showModal();
				this.settingValue.loadingList = false;
			});
	}

	addNew() {
		this.selectedItem = {};
		this.form = this.createForm();
		this.showModal();
	}

	createForm(data?: ExtendedDelay) {
		return this.fb.group({
			iataNum: [data ? data.iataNum : null, [Validators.required]],
			iataChar: [
				data ? data.iataChar : '',
				[Validators.required, this.formValidators.onlyEnglishAndNumbers(), this.formValidators.minLengthValidator(2)],
			],
			nameEn: [data ? data.nameEn : '', [Validators.required, this.formValidators.onlyEnglish()]],
			nameRu: [data ? data.nameRu : '', [Validators.required]],
			dtRangeStart: [data ? convertDateToUTC(new Date(data.dtRangeStart)) : new Date(), [Validators.required]],
			dtRangeFinish: [
				data ? convertDateToUTC(new Date(data.dtRangeFinish)) : new Date('2099-12-31 23:59'),
				[Validators.required],
			],
		});
	}

	add() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.insertRecord('delays', null, this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => {
					this.displayError(err);
				},
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	save() {
		this.form.markAllAsTouched();
		if (this.form.valid) {
			this.settingValue.saving = true;
			this.apiMasterData.updateRecord('delays', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
				next: () => {
					this.closeModal();
					this.loadList();
				},
				error: err => this.displayError(err),
				complete: () => {
					this.settingValue.saving = false;
				},
			});
		}
	}

	delete() {
		this.settingValue.saving = true;
		this.form.get('dtRangeFinish').setValue(new Date());
		this.apiMasterData.updateRecord('delays', this.selectedItem.id.toString(), this.createFromForm()).subscribe({
			next: () => {
				this.closeModal();
				this.loadList();
			},
			error: err => this.displayError(err),
			complete: () => {
				this.settingValue.saving = false;
			},
		});
	}

	duplicate() {
		this.selectedItem.id = undefined;
		this.selectedItem.lastupdate = undefined;
	}

	showDeleteConfirm(): void {
		this.modal.confirm({
			nzTitle: this.globalI18n.getMessage(Module.MasterData, 'deleteItem'),
			nzOkText: this.globalI18n.getMessage(Module.MasterData, 'yes'),
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.delete(),
			nzCancelText: this.globalI18n.getMessage(Module.MasterData, 'no'),
			nzOnCancel: () => console.log('Cancel'),
		});
	}

	createFromForm(): Delay {
		return {
			...this.selectedItem,
			...this.form.value,
			name: [this.form.get('nameEn').value, this.form.get('nameRu').value],
			dtRange: [this.form.get('dtRangeStart').value, this.form.get('dtRangeFinish').value],
		};
	}

	downloadCsvFile() {
		this.functionsService.downloadCsvFile('delays', this.settingValue.loadingList);
	}

	downloadXmlFile() {
		this.functionsService.downloadXmlFile('delays', this.settingValue.loadingList);
	}

	onPageIndexChanged(index: number) {
		this.settingValue.pageIndex = index;
	}

	showModal(): void {
		this.settingValue.isVisibleModal = true;
	}

	closeModal(): void {
		this.settingValue.isVisibleModal = false;
	}

	displayError(err): void {
		this.modal.create({
			nzTitle: 'Error',
			nzContent: err.message,
			nzClosable: false,
		});
	}
}
